import React, { useContext, useState, useEffect } from "react";
import { Button, Datagrid, Icon, Spinner } from "@abb/abb-common-ux-react";
import { RootContext } from "../../contexts/root-context";
import NotificationExample from "../../components/Notification/notification";
import "./configure-sld.css";
import { FILE_UPLOAD_API_URL, WNT_BASE_API_URL, WNT_ERROR_LIST } from "../../strings";
import { getToken } from '../../components/Authentication/get-token';

const ConfigureSLD = () => {
  const { setShowConfigureSLDPage, setUploadedSLD, isnotify, setisnotify } = useContext(RootContext);
  const [selectedBusConfigFile, setSelectedBusConfigFile] = useState(null);
  const [selectedSLDFile, setSelectedSLDFile] = useState(null);
  const [sldFileURL, setSldFileURL] = useState(null);
  const [sldFileLastUpdated, setSldFileLastUpdated] = useState(null);
  const [busConfigFileLastUpdated, setBusConfigFileLastUpdated] = useState(null);
  const [isAllUploading, setIsAllUploading] = useState(false);
  const [isAllUploaded, setIsAllUploaded] = useState(false);
  const [isSLDUploaded, setIsSLDUploaded] = useState(false);
  const [isBusConfigUploaded, setIsBusConfigUploaded] = useState(false);
  const [sldFileChanged, setSldFileChanged] = useState(false);
  const [busConfigFileChanged, setBusConfigFileChanged] = useState(false);
  const [busConfigData, setBusConfigData] = useState([]);
  const [isSpin, setIsSpin] = useState(true);
  const [error, setError] = useState(false);

  const goBack = () => {
    setShowConfigureSLDPage(false);
  };

  const getAuthHeaders = () => {
    const tokenObj = getToken();
    return {
        "Content-Type": "application/json",
        ...(tokenObj?.headers || {}),
    };
  };

  const handleBusConfigFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedBusConfigFile(file);
      setBusConfigFileLastUpdated(new Date().toISOString());
      setIsBusConfigUploaded(false);
      setBusConfigFileChanged(true);
    }
  };

  const handleSLDFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      setisnotify("");
      if (file.size > 5 * 1024 * 1024) {
        setisnotify("sld-too-large");
        return;
      }
      if (!validTypes.includes(file.type)) {
        setisnotify("sld-invalid-format");
        return;
      }
      const url = URL.createObjectURL(file);
      setSldFileURL(url);
      setUploadedSLD(url);
      setSelectedSLDFile(file);
      setSldFileLastUpdated(new Date().toISOString());
      setIsSLDUploaded(false);
      setSldFileChanged(true);
    }
  };

  const uploadSLDFile = async () => {
    if (!selectedSLDFile || !(selectedSLDFile instanceof Blob)) {
      console.error("Selected file is invalid or not of type Blob.");
      setisnotify("sld-file-upload-failed");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result.split(",")[1];
      const fileName = selectedSLDFile.name;
      const payload = {
        fileName,
        data: base64,
        projectName: "wnt",
        fileType: "sld",
      };
      try {
        const response = await fetch(`${FILE_UPLOAD_API_URL}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          setIsSLDUploaded(true);
          localStorage.setItem("selectedSLDFile", fileName);
        } else {
          setisnotify("sld-file-upload-failed");
        }
      } catch (error) {
        console.error("SLD upload error:", error);
        setisnotify("sld-file-upload-failed");
      }
    };  
    reader.readAsDataURL(selectedSLDFile);
  };

  const uploadBusConfigFile = async () => {
    if (!selectedBusConfigFile || !(selectedBusConfigFile instanceof Blob)) {
      console.error("Selected Bus Config file is invalid.");
      setisnotify("bus-config-upload-failed");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const base64 = event.target.result.split(",")[1];
        const fileName = selectedBusConfigFile.name;

        const payload = {
          fileName,
          data: base64,
          projectName: "wnt",
          fileType: "networkdata"
        };

        const response = await fetch(`${FILE_UPLOAD_API_URL}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          setIsBusConfigUploaded(true);
          localStorage.setItem("selectedBusConfigFile", fileName);
        } else {
          console.error("Upload failed with status:", response.status);
          setisnotify("bus-config-upload-failed");
        }
      } catch (error) {
        console.error("Bus config upload error:", error);
        setisnotify("bus-config-upload-failed");
      }
    };

    reader.readAsDataURL(selectedBusConfigFile);
  };

  const handleUpload = async () => {
    if (!selectedBusConfigFile && !selectedSLDFile) 
      return;

    setisnotify("upload-inprogress");
    setIsAllUploading(true);
    setIsAllUploaded(false);
    setError(false);

    try {
      const filesToUpload = [];

      const prepareFile = async (file, projectName, fileType) => {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const base64String = e.target.result.split(",")[1];
            resolve(base64String);
          };
          reader.onerror = (err) => reject(err);
          reader.readAsDataURL(file);
        });

        return {
          fileName: file.name,
          data: base64,
          projectName,
          fileType,
        };
      };

      // Only include files that changed
      if (sldFileChanged && selectedSLDFile) {
        const sldPayload = await prepareFile(selectedSLDFile, "wnt", "sld");
        filesToUpload.push(sldPayload);
      }

      if (busConfigFileChanged && selectedBusConfigFile) {
        const busConfigPayload = await prepareFile(selectedBusConfigFile, "wnt", "networkdata");
        filesToUpload.push(busConfigPayload);
      }

      if (filesToUpload.length === 0) {
        setisnotify("nothing-to-upload");
        return;
      }

      const response = await fetch(FILE_UPLOAD_API_URL, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ files: filesToUpload }),
      });

      if (response.ok) {
        if (sldFileChanged) {
          setIsSLDUploaded(true);
          localStorage.setItem("selectedSLDFile", selectedSLDFile.name);
        }
        if (busConfigFileChanged) {
          setIsBusConfigUploaded(true);
          localStorage.setItem("selectedBusConfigFile", selectedBusConfigFile.name);
          await new Promise((resolve) => setTimeout(resolve, 2500));
          await fetchBusConfigData();
        }

        setIsAllUploaded(true);
        setSldFileChanged(false);
        setBusConfigFileChanged(false);

        setisnotify("upload-success");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Unified upload error:", error);
      setError(true);
      setisnotify("upload-failure");
    } finally {
      setTimeout(() => setisnotify(""), 3000);
      setIsAllUploading(false);
    }
  };

  const fetchFileMetadataDetails = async () => {
    try {
      const projectName = "wnt";
      const metadataResponse = await fetch(`${WNT_BASE_API_URL}/file-metadata?projectName=${projectName}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const result = await metadataResponse.json();
      if (metadataResponse.ok && result?.data) {
        result.data.forEach((file) => {
          const { fileType, uploadedOn, fileName } = file;
          if (fileType === "sld") {
            setSelectedSLDFile({ name: fileName });
            setSldFileLastUpdated(uploadedOn);
            setIsSLDUploaded(true);
            localStorage.setItem("selectedSLDFile", fileName);
          } else if (fileType === "networkdata") {
            setSelectedBusConfigFile({ name: fileName });
            setBusConfigFileLastUpdated(uploadedOn);
            setIsBusConfigUploaded(true);
          }
        });
      }
    } catch (error) {
      setError(true);
    }
  };

  useEffect(() => {
    fetchFileMetadataDetails();
  }, []);
  
  const fetchBusConfigData = async () => {
    setIsSpin(true);
    try {
      const response = await fetch(`${WNT_BASE_API_URL}/network-data`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const json = await response.json();
      let busData = json.data;
      let formatedData = busData.map((v) => ({
        fields: {
          BusName: v.BusName,
          BusType: v.BusType,
          Latitude: v.Latitude,
          Longitude: v.Longitude,
          Voltage_rating_kV: v.Voltage_rating_kV,
        },
      }));
      setBusConfigData(formatedData);
      setError(false);
      setIsSpin(false);
    } catch (error) {
      console.error("Bus config fetch error:", error);
      setIsSpin(false);
      setError(true);
    }
  };
  
  useEffect(() => {
    fetchBusConfigData();
  }, []);

  const getTimeAgo = (date) => {
    if (!date) return "N/A";
    const current = new Date();
    const past = new Date(date);
    const seconds = Math.floor((current - past) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    if (months > 0) return `${months} month${months > 1 ? "s" : ""} ago`;
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (seconds >= 0) return `${seconds} sec${seconds > 1 ? "s" : ""} ago`;
    return "Just now";
  };

  const sldFileDisplay = selectedSLDFile && (
    <>
      <b>{selectedSLDFile.name}</b>
      <Icon className="uploaded" name="abb/check-mark-circle-1" />
      <span className="wnt-file-upload-notification">
        Last updated on : {sldFileLastUpdated ? getTimeAgo(sldFileLastUpdated) : "N/A"}
      </span>
    </>
  );

  const busConfigExcelFileDisplay = selectedBusConfigFile && (
    <>
      <b>{selectedBusConfigFile.name}</b>
      <Icon className="uploaded" name="abb/check-mark-circle-1" />
      <span className="nw-file-upload-notification">
        Last updated on : {busConfigFileLastUpdated ? getTimeAgo(busConfigFileLastUpdated) : "N/A"}
      </span>
    </>
  );

  const fileName = "BusConfigurationMappingSample.xlsx";
  const fileUrl = `${process.env.PUBLIC_URL}/${fileName}`;

  const canUpload = selectedSLDFile && selectedBusConfigFile && (sldFileChanged || busConfigFileChanged) && !isAllUploading;

  const initialColumns = React.useMemo(() => [
    { fieldKey: 'BusName', title: 'Bus Name' },
    { fieldKey: 'BusType', title: 'Bus Type' },
    { fieldKey: 'Latitude', title: 'Latitude' },
    { fieldKey: 'Longitude', title: 'Longitude'},
    { fieldKey: 'Voltage_rating_kV', title: 'Voltage Rating (kV)'},
  ], []);

  const [currentPage, setCurrentPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(12);
  const [columns, setColumns] = React.useState(initialColumns);
  const [sortOrder, setSortOrder] = React.useState(undefined);
  const [columnOrder, setColumnOrder] = React.useState(undefined);
      
  return (
    <>
    {["upload-inprogress", "upload-success", "upload-failure", "sld-too-large", "sld-invalid-format", "sld-file-upload-failed", "bus-config-upload-failed"].includes(isnotify) && (
      <NotificationExample notitype={isnotify} />
    )}
      <div className="configure-sld-page-content">
        <div className="wnt-row">
          <span className="wnt-go-back" onClick={goBack}>
            <Icon sizeClass="small" name="abb/left-arrow" />
            {"  "}Back
          </span>
        </div>
        {busConfigData.length === 0 && (
          <div className="wnt-row">
            <span className="wnt-sub-page-title">SLD Configuration</span>
          </div>
        )}

        <div className="wnt-row">
          <span className="wnt-inside-header">Upload Single Line Diagram</span>
        </div>
        <div className="wnt-row">
          <input
            type="file"
            accept=".jpg,.jpeg,.png"
            id="inputField"
            className="wnt-ip-nw-file"
            onChange={handleSLDFileChange}
          />
        </div>
        <span style={{ display: "flex", fontSize: "12px", marginTop:"2px", marginBottom:"10px"}}>
          {sldFileDisplay}
        </span>
        <div className="wnt-row">
          <span className="wnt-inside-header">Upload Bus Configuration</span>
        </div>
        <div className="wnt-row">
          <input
            type="file"
            accept=".xlsx, .xls"
            className="wnt-ip-nw-file"
            onChange={handleBusConfigFileChange}
          />
        </div>
        <span style={{ display: "flex", fontSize: "12px", marginTop:"2px", marginBottom:"10px" }}>
          {busConfigExcelFileDisplay}
        </span>
        <div className="wnt-row">
          <a href={fileUrl} download={fileName} className="wnt-inside-header">
            Download sample template
          </a>
        </div>
        <div className="wnt-row">
          <div className="wnt-button_row">
            <Button
              text={isAllUploading ? "Uploading..." : "Upload"}
              disabled={!canUpload}
              shape="pill"
              sizeClass="small"
              type="primary-black"
              onClick={handleUpload}
            />
            <Button text="Cancel" shape="pill" sizeClass="small" type="normal" onClick={goBack} />
          </div>
        </div>
        {busConfigData?.length > 0 && (
          <>
          {Array.isArray(busConfigData) ? (
          <>
            <h5 className="table-title">Bus List</h5>
            {isSpin ? (
              <Spinner style={{ margin: "auto" }} color="dark-grey" size="small" />
            ) : busConfigData.length > 0 ? (
              <Datagrid
                style={{ height: "400px",width:"90%" }}
                monochrome={true}
                data={busConfigData}
                columns={columns}
                pageSize={pageSize}
                page={currentPage}
                rowHeight="small"
                enableSorting={true}
                sortOrder={sortOrder}
                onSort={(so) => setSortOrder(so)}
                columnOrder={columnOrder}
              />
            ) : (
              <p className="center-text-error">{WNT_ERROR_LIST.NO_DATA}</p>
            )}
          </>
        ) : (
          <p className="center-text-error">{WNT_ERROR_LIST.NO_DATA}</p>
        )}
        </>
        )}
      </div>
    </>
  );
};

export default ConfigureSLD;